import React, { useEffect, useId } from "react";
import { useLocation } from "react-router-dom";

/** Banner placement (news / transfer pages). */
export const YEKTANET_BANNER_POS_ID = "ynpos-20926";
/** Home hub banner placement. */
export const YEKTANET_HOME_POS_ID = "ynpos-20927";

declare global {
  interface Window {
    yektanet?: ((...args: unknown[]) => void) & { q?: unknown[] };
    EventBus?: { emit: (event: string, detail?: unknown) => void };
  }
}

type YektanetBannerProps = {
  className?: string;
  /** DOM id Yektanet targets, e.g. ynpos-20926 */
  posId?: string;
  /** Extra classes on the placement node (banner uses yn-bnr). */
  placementClassName?: string;
};

let refreshTimer = 0;
let refreshGeneration = 0;

/**
 * Debounced full reset so multiple banners on one page don't race each other.
 * Needed because publisher config has is_spa=false.
 */
function scheduleYektanetRefresh(posIds: string[]) {
  const gen = ++refreshGeneration;
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    if (gen !== refreshGeneration) return;
    if (!window.EventBus || typeof window.EventBus.emit !== "function") {
      // Script not ready yet — retry a few times from the caller.
      return;
    }
    for (const id of posIds) {
      const el = document.getElementById(id);
      if (el) el.innerHTML = "";
    }
    try {
      window.EventBus.emit("reset-ads");
    } catch {
      // ignore
    }
  }, 80);
}

function waitForEventBusAndRefresh(posId: string) {
  let attempts = 0;
  let timer = 0;
  let cancelled = false;

  const tick = () => {
    if (cancelled) return;
    if (window.EventBus && typeof window.EventBus.emit === "function") {
      // Collect every known placement currently in the DOM.
      const ids = Array.from(
        document.querySelectorAll<HTMLElement>(
          ".yn-bnr[id], [id^='ynpos-'], [id^='pos-article-display-']"
        )
      )
        .map((el) => el.id)
        .filter(Boolean);
      scheduleYektanetRefresh(ids.length ? ids : [posId]);
      return;
    }
    if (attempts++ < 25) {
      timer = window.setTimeout(tick, 120);
    }
  };

  timer = window.setTimeout(tick, 50);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}

const YektanetBanner: React.FC<YektanetBannerProps> = ({
  className = "",
  posId = YEKTANET_BANNER_POS_ID,
  placementClassName = posId.startsWith("ynpos-") ? "yn-bnr" : "",
}) => {
  const { pathname } = useLocation();
  const visitKey = `${pathname}:${posId}:${useId()}`;

  useEffect(() => waitForEventBusAndRefresh(posId), [pathname, visitKey, posId]);

  return (
    <aside
      className={`w-full overflow-hidden ${className}`}
      aria-label="advertisement"
      data-ad-network="yektanet"
    >
      <div key={visitKey} className={placementClassName} id={posId} />
    </aside>
  );
};

export default YektanetBanner;
